<template>
    <InputDropdown
        ref="inputDropdown"
        :model-value="modelValue"
        :initial-value="modelValue"
        :items="items"
        :item-label="getLabel"
        :item-value="getValue"
        :error="error"
        :disabled="disabled || isChangingLang"
        :options-loading="isChangingLang"
        key-field="languageCode"
        required
        hide-search
        hide-selected-option
        v-bind="bindings"
        class="language-selector"
        @update:model-value="handleChange"
    >
        <template #dropdown-selected-option="{ selectedItemLabel, selectedItemValue }">
            <div
                v-if="selectedItemValue"
                class="option-container"
            >
                <img
                    v-if="displayFlags && selectedItemValue"
                    :src="getFlagUrl(selectedItemValue)"
                    :srcset="getFlagUrlHiRes(selectedItemValue)"
                    @error="onFlagLoadError"
                />
                <Words
                    oneline
                    :class="{ 'text-break': selectedItemValue }"
                >{{ selectedItemLabel }}</Words>
            </div>
        </template>

        <template #dropdown-option="{ item }">
            <div class="option-container">
                <img
                    v-if="displayFlags"
                    :src="getFlagUrl(getValue(item))"
                    :srcset="getFlagUrlHiRes(getValue(item))"
                    @error="onFlagLoadError"
                />
                <Words
                    oneline
                    class="text-break"
                >{{ getLabel(item) }}</Words>
            </div>
        </template>
    </InputDropdown>
</template>

<script setup lang="ts">
import { omit } from 'ramda'
import { computed, ref } from 'vue'
import { useEventBus } from '@vueuse/core'
import InputDropdown from 'widget-components/components/InputDropdown/InputDropdown.vue'
import type { BlockPath } from 'widget-components/integrations/CustomPage/types'
import type { InputDropdownItem, InputDropdownProps } from '../InputDropdown/types'
import Words from 'widget-components/components/Words/Words.vue'
import { PALE_PLACEHOLDER_IMAGE_URL } from 'widget-components/utilities/placeholders'
import { DEFAULT_LANGUAGE_ITEMS, LANGUAGE_TO_COUNTRY, languageCodeToItem, type LanguageSelectorItem } from './language-data'
import { useI18n } from 'widget-components/utilities/i18n'
import equals from 'fast-deep-equal/es6'

const isChangingLang = ref(false)
useEventBus<boolean>('lang-change-loading').on((loading) => {
    isChangingLang.value = loading
})

const { locale } = useI18n()

const getLabel = (item: LanguageSelectorItem) => props.displayNames ? item.label : item.languageCode.toUpperCase()
const getValue = (item: LanguageSelectorItem) => item.languageCode

const getFlagUrl = (languageCode: string) => {
    const countryCode = LANGUAGE_TO_COUNTRY[languageCode] || languageCode
    return `https://flagcdn.com/w40/${countryCode.toLowerCase()}.webp`
}

const getFlagUrlHiRes = (languageCode: string) => {
    const countryCode = LANGUAGE_TO_COUNTRY[languageCode] || languageCode
    return `https://flagcdn.com/w80/${countryCode.toLowerCase()}.webp 2x`
}

defineOptions({ inheritAttrs: false })

const props = defineProps<{
    id: string
    error?: string | boolean
    disabled?: boolean
    languages?: string[]
    label?: string
    displayFlags?: boolean
    displayNames?: boolean
    blockPath?: BlockPath
    placeholder?: string
}>()

const error = computed(() => props.error)

const items = computed(() => {
    if (props.languages?.length) {
        const langs = [...props.languages]
        return langs.map(languageCodeToItem)
    }

    return DEFAULT_LANGUAGE_ITEMS
})

const modelValue = computed(() => {
    // set fallback to EN if locale not presented in items
    return items.value.find(i => i.languageCode === locale.value) as InputDropdownItem ?? languageCodeToItem('en')
})

const emit = defineEmits<{
    (e: 'change-lang', lang: string): void
}>()

const bindings = computed<Partial<InputDropdownProps>>(() => ({
    ...omit(['languages', 'error', 'disabled', 'displayFlags'], props),
}))

const onFlagLoadError = (event: Event) => {
    const imgElement = event.target as HTMLImageElement
    imgElement.src = PALE_PLACEHOLDER_IMAGE_URL
    imgElement.removeAttribute('srcset')
}

const handleChange = (val: LanguageSelectorItem) => {
    if (!equals(val, modelValue.value)) {
        emit('change-lang', val.languageCode)
    }
}
</script>

<style lang="sass" scoped>
@import './LanguageSelector.sass'
</style>
